import { useEffect, useRef, useState } from 'react';
import { CHAT_CONSTANTS } from '../config/chat.constants';
import { Message } from '../models/models';

export const useAutoScroll = (
  messages: Message[] | undefined,
  isGenerating: boolean | undefined,
  isActuallyGenerating: boolean
) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFirstUpdate, setIsFirstUpdate] = useState(true);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const intervalIdRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isNearBottom && isActuallyGenerating) {
      return;
    }

    const scrollToBottom = () => {
      if (containerRef.current) {
        requestAnimationFrame(() => {
          if (containerRef.current) {
            containerRef.current.scrollTo({
              top: containerRef.current.scrollHeight,
              behavior: isFirstUpdate ? 'auto' : 'smooth'
            });

            if (isFirstUpdate) {
              setIsFirstUpdate(false);
            }
          }
        });
      }
    };

    const timeoutId = setTimeout(scrollToBottom, CHAT_CONSTANTS.SCROLL_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, [messages, isFirstUpdate, isNearBottom, isActuallyGenerating]);

  useEffect(() => {
    if (isGenerating) {
      setIsNearBottom(true);
    }
  }, [isGenerating]);

  useEffect(() => {
    if (!isActuallyGenerating) {
      return;
    }

    const handleScroll = () => {
      if (containerRef.current) {
        const {scrollTop, scrollHeight, clientHeight} = containerRef.current;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        const shouldBeNearBottom = distanceFromBottom < CHAT_CONSTANTS.SCROLL_THRESHOLD_PX;

        setIsNearBottom(shouldBeNearBottom);
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isActuallyGenerating]);

  useEffect(() => {
    if (!isActuallyGenerating || !isNearBottom) {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
      return;
    }

    const scrollToBottom = () => {
      if (containerRef.current) {
        requestAnimationFrame(() => {
          if (containerRef.current) {
            containerRef.current.scrollTo({
              top: containerRef.current.scrollHeight,
              behavior: 'smooth'
            });
          }
        });
      }
    };

    const intervalId = setInterval(scrollToBottom, CHAT_CONSTANTS.SCROLL_INTERVAL_MS);
    intervalIdRef.current = intervalId;

    return () => {
      clearInterval(intervalId);
      intervalIdRef.current = null;
    };
  }, [isActuallyGenerating, isNearBottom]);

  return { containerRef, isNearBottom };
};
